import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { rowsOf } from '../map'

export const BOARD_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export type BoardPriority = (typeof BOARD_PRIORITIES)[number]

export type BoardColumn = { key: string; name: string; position: number; isDone: boolean; wipLimit: number | null }
export type Board = { id: string; code: string; name: string; description: string; isDefault: boolean; columns: BoardColumn[] }

export type BoardTask = {
  id: string
  boardId: string
  columnKey: string
  key: string
  title: string
  description: string
  assigneeId: string
  priority: BoardPriority
  dueAt: string | null
  accountId: string
  projectId: string
  taskType: string
  labels: string[]
  checklistDone: number
  checklistTotal: number
  completedAt: string | null
  createdAt: string
}

export type BoardTaskInput = {
  title: string
  description: string
  columnKey: string
  assigneeId: string
  priority: BoardPriority
  dueDate: string
  accountId: string
  projectId: string
}

type Row = Record<string, unknown>
const str = (v: unknown) => (v == null ? '' : String(v))

function mapColumn(r: Row): BoardColumn {
  const wip = Number(r.wip_limit)
  return { key: str(r.key), name: str(r.name), position: Number(r.position) || 0, isDone: r.is_done === true, wipLimit: Number.isFinite(wip) && wip > 0 ? wip : null }
}

function mapBoard(r: Row): Board {
  const columns = Array.isArray(r.columns) ? (r.columns as Row[]).map(mapColumn).sort((a, b) => a.position - b.position) : []
  return { id: str(r.id), code: str(r.code), name: str(r.name), description: str(r.description), isDefault: r.is_default === true, columns }
}

function mapTask(r: Row): BoardTask {
  const priority = str(r.priority) as BoardPriority
  return {
    id: str(r.id),
    boardId: str(r.board_id),
    columnKey: str(r.column_key),
    key: str(r.task_key),
    title: str(r.title),
    description: str(r.description),
    assigneeId: str(r.assignee_user_id),
    priority: BOARD_PRIORITIES.includes(priority) ? priority : 'normal',
    dueAt: r.due_at ? str(r.due_at) : null,
    accountId: str(r.account_id),
    projectId: str(r.project_id),
    taskType: str(r.task_type),
    labels: Array.isArray(r.labels) ? r.labels.map(String) : [],
    checklistDone: Number(r.checklist_done) || 0,
    checklistTotal: Number(r.checklist_total) || 0,
    completedAt: r.completed_at ? str(r.completed_at) : null,
    createdAt: str(r.created_at),
  }
}

const boardKeys = {
  boards: ['boards'] as const,
  tasks: (boardId: string) => ['boards', boardId, 'tasks'] as const,
}

export const useBoards = () =>
  useQuery({ queryKey: boardKeys.boards, queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/boards')).map(mapBoard), staleTime: 60_000 })

export const useBoardTasks = (boardId: string) =>
  useQuery({
    queryKey: boardKeys.tasks(boardId),
    queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/tasks', { board_id: boardId })).map(mapTask),
    enabled: !!boardId,
  })

function useInvalidate() {
  const qc = useQueryClient()
  // Proje ayrıntısı ve "benim görevlerim" de aynı görevleri gösterir.
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['boards'] }), qc.invalidateQueries({ queryKey: ['projects'] }), qc.invalidateQueries({ queryKey: ['tasks'] })])
}

/** Kolon değişikliği ekranda hemen uygulanır; WIP sınırı vb. hata olursa geri alınır. */
export function useMoveBoardTask(boardId: string) {
  const qc = useQueryClient()
  const invalidate = useInvalidate()
  const key = boardKeys.tasks(boardId)
  return useMutation({
    mutationFn: ({ taskId, columnKey }: { taskId: string; columnKey: string }) => api.put(`/api/v1/tasks/${taskId}`, { column_key: columnKey }),
    onMutate: async ({ taskId, columnKey }) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<BoardTask[]>(key)
      if (previous) qc.setQueryData<BoardTask[]>(key, previous.map((t) => (t.id === taskId ? { ...t, columnKey } : t)))
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous)
    },
    onSettled: () => invalidate(),
  })
}

export function useSaveBoardTask(boardId: string) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: BoardTaskInput }) => {
      const body = {
        title: input.title.trim(),
        description: input.description,
        column_key: input.columnKey,
        assignee_user_id: input.assigneeId,
        priority: input.priority,
        due_at: input.dueDate,
        account_id: input.accountId,
        project_id: input.projectId,
      }
      return id ? api.put(`/api/v1/tasks/${id}`, body) : api.post('/api/v1/tasks', { ...body, board_id: boardId })
    },
    onSuccess: () => invalidate(),
  })
}

export function useArchiveBoardTask() {
  const invalidate = useInvalidate()
  return useMutation({ mutationFn: (id: string) => api.post(`/api/v1/tasks/${id}/archive`, {}), onSuccess: () => invalidate() })
}

export function useBoardColumnMutations(boardId: string) {
  const invalidate = useInvalidate()
  const save = useMutation({
    mutationFn: (c: BoardColumn) =>
      api.put(`/api/v1/boards/${boardId}/columns/${c.key}`, { name: c.name.trim(), position: c.position, is_done: c.isDone, wip_limit: c.wipLimit }),
    onSuccess: () => invalidate(),
  })
  const remove = useMutation({ mutationFn: (key: string) => api.del(`/api/v1/boards/${boardId}/columns/${key}`), onSuccess: () => invalidate() })
  return { save, remove }
}
